import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import DetailItem from '../../../../Views/Pages/Employer/employer-details/basic-details/DetailItem';
import { useDispatch } from 'react-redux';
import { showSuccessOrError } from '../../../../services/commonService';
import CustomSpinner from '../../../ReusableComponents/CustomSpinner';
import SubmitSpinnerButton from '../../../ReusableComponents/SubmitSpinnerButton';
import translation from '../../../../utils/translation';
import { fetchInvoiceDetails, updatePaymentFollowUp } from '../../../../redux/Slices/invoices/InvoicesSlice';
import configData from '../../../ReusableComponents/app-configs';

function PaymentFollowUpPopupView({ onClose, isEditMode = false, paymentFollowUpId }) {
  const dispatch = useDispatch();
  const [viewMode, setViewMode] = useState(!isEditMode);
  const [followUpDetails, setFollowUpDetails] = useState({});
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('idle');

  console.log(followUpDetails, 'followUpDetails');

  const { register, handleSubmit, setValue, formState: { errors } } = useForm({
    defaultValues: configData.paymentFollowUpFormState
  });

  useEffect(() => {
    setIsLoadingContent(true);
    // Fetch payment follow-up details and related invoice details
    dispatch(fetchInvoiceDetails({ invoice_id: paymentFollowUpId })).then((res) => {
      if (res?.payload?.data) {
        const paymentData = res.payload.data;
        setFollowUpDetails(paymentData);
      }
      setIsLoadingContent(false);
    });
  }, [dispatch, paymentFollowUpId]);

  useEffect(() => {
    if (followUpDetails) {
      setValue('follow_up_note', followUpDetails.follow_up_note || '');
      
      // Handle date extraction
      if (followUpDetails.due_date) {
        const dateObj = new Date(followUpDetails.due_date);
        
        // Format date as YYYY-MM-DD for input[type="date"]
        const formattedDate = dateObj.toISOString().split('T')[0];
        setValue('due_date', formattedDate);
        
        // Format time as HH:MM for input[type="time"] 
        const formattedTime = dateObj.toISOString().substr(11, 5); // Gets HH:MM format in UTC
        setValue('due_time', formattedTime);
      }
      
      // Fallback for separate due_time field if it exists
      if (followUpDetails.due_time && !followUpDetails.due_date) {
        setValue('due_time', followUpDetails.due_time);
      }
    }
  }, [followUpDetails, setValue]);

  const toggleEditMode = () => {
    setViewMode(!viewMode);
  };

  const onSubmit = (data) => {
    // Properly format the datetime string
    const formattedDateTime = `${data.due_date} ${data.due_time}:00`; // Add seconds
    
    const payload = {
      invoice_id: paymentFollowUpId,
      follow_up_note: data?.follow_up_note,
      due_date: formattedDateTime,
    }

    setSubmitStatus('loading');
    console.log("Form submitted with data:", payload);

    dispatch(updatePaymentFollowUp(payload)).then((res) => {
      showSuccessOrError(res);
      setSubmitStatus('success');
      if (res?.payload?.data) {
        setIsLoadingContent(true);
        dispatch(fetchInvoiceDetails({ invoice_id: paymentFollowUpId })).then((res) => {
          if (res?.payload?.data) {
            const paymentData = res.payload.data;
            setFollowUpDetails(paymentData);
          }
          setIsLoadingContent(false);
        });
      }
      setViewMode(true);
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).replace(/\//g, '-');
  };

  const formatTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    
    try {
      // Extract time directly from ISO string to avoid timezone conversion
      if (dateTimeString.includes('T')) {
        return dateTimeString.split('T')[1].substr(0, 5); // Gets HH:MM from ISO string
      }
      
      const date = new Date(dateTimeString);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return dateTimeString; // Return original if it's not a valid date
      }
      
      // Use UTC time to avoid timezone conversion
      return date.toISOString().substr(11, 5);
    } catch (error) {
      console.error('Error formatting time:', error);
      return dateTimeString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0';
    return `$${Number(amount).toLocaleString()}`;
  };

  // Calculate balance amount
  const calculateBalance = () => {
    const totalAmount = followUpDetails?.total_amount;
    const paidAmount = followUpDetails?.paid_amount;
    return totalAmount - paidAmount;
  };

  return (
    <div
      className="modal fade modalCustom addAddress show"
      tabIndex={-1}
      aria-labelledby="exampleModalCenterTitle"
      style={{
        display: "block",
        background: "rgba(0, 0, 0, 0.7)",
      }}
      aria-modal="true"
      role="dialog"
    >
      <div className="modal-dialog" role="document" style={{ maxWidth: '665px' }}>
        <div className="modal-content br20">
          <div className="modal-body p32">
            <div className="d-flex justify-content-between align-items-center mb24">
              <h5 className="fz20 font-roboto fw-500 black">
                {translation('key_payment') + ' ' + translation('key_follow_up')}
                <p className="m-0 fz16 fw-400 mt-1">
                  {followUpDetails?.invoice_number}
                </p>
              </h5>
              <div className="d-flex align-items-center">
                {viewMode && (
                  <>
                    <i className="ri-pencil-fill mr-2 cursor-pointer blue fz20"
                      onClick={toggleEditMode}></i>
                  </>
                )}
                <i className="ri-close-line cursor-pointer grey2 fz20" onClick={onClose}></i>
              </div>
            </div>

            {isLoadingContent ? <CustomSpinner /> :
              <>
                {viewMode ? (
                  <div className="view-form">
                    <DetailItem
                      title={translation('key_employer_name')}
                      desc={followUpDetails?.employer_name || 'N/A'}
                      columnSize='mb24'
                    />
                    <DetailItem
                      title={'Worker Name'}
                      desc={followUpDetails?.worker_name}
                      columnSize='mb24'
                    />

                    <div className="form-row">
                      <div className="col-4">
                        <DetailItem
                          title={'Date'}
                          desc={formatDate(followUpDetails?.due_date)}
                          columnSize='mb24'
                        />
                      </div>
                      <div className="col-4">
                        <DetailItem
                          title={'Time'}
                          desc={formatTime(followUpDetails?.due_date)}
                          columnSize='mb24'
                        />
                      </div>
                    </div>

                    {/* Payment Details Section */}
                    <div className="form-row">
                      <div className="col-4">
                        <DetailItem
                          title={'Total Amount'}
                          desc={formatCurrency(followUpDetails?.total_amount)}
                          columnSize='mb24'
                        />
                      </div>
                      <div className="col-4">
                        <DetailItem
                          title={'Amount Paid'}
                          desc={formatCurrency(followUpDetails?.paid_amount)}
                          columnSize='mb24'
                        />
                      </div>
                      <div className="col-4">
                        <DetailItem
                          title={'Balance Amount'}
                          desc={formatCurrency(calculateBalance())}
                          columnSize='mb24'
                        />
                      </div>
                    </div>
                    <DetailItem
                      title={'Follow Up Notes'}
                      desc={followUpDetails?.follow_up_note || ''}
                      columnSize='mb24'
                    />
                  </div>
                ) : (
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <DetailItem
                      title={translation('key_employer_name')}
                      desc={followUpDetails?.employer_name || 'N/A'}
                      columnSize='mb24'
                    />
                    <DetailItem
                      title={'Worker Name'}
                      desc={followUpDetails?.worker_name}
                      columnSize='mb24'
                    />

                    {/* Non-editable Payment Details */}
                    <div className="form-row">
                      <div className="col">
                        <DetailItem
                          title={'Total Amount'}
                          desc={formatCurrency(followUpDetails?.total_amount)}
                          columnSize='mb24'
                        />
                      </div>
                      <div className="col">
                        <DetailItem
                          title={'Amount Paid'}
                          desc={formatCurrency(followUpDetails?.paid_amount)}
                          columnSize='mb24'
                        />
                      </div>
                    </div>

                    <DetailItem
                      title={'Balance Amount'}
                      desc={formatCurrency(calculateBalance())}
                      columnSize='mb24'
                    />

                    {/* Editable Follow Up Notes */}
                    <div className="form-group mb-4">
                      <label className="form-label required">{translation('key_follow_up_notes')}</label>
                      <textarea
                        className={`form-control ${errors.follow_up_note ? 'is-invalid' : ''}`}
                        rows="3"
                        {...register("follow_up_note", { required: true })}
                      ></textarea>
                      {errors.follow_up_note && <div className="invalid-feedback">{translation('key_this_field_is_required')}</div>}
                    </div>

                    {/* Editable Date and Time */}
                    <div className="row mb-3">
                      <div className="col">
                        <div className="form-group">
                          <label className="form-label required">{translation('key_date')}</label>
                          <input
                            type="date"
                            className={`form-control ${errors.due_date ? 'is-invalid' : ''}`}
                            {...register("due_date", { required: true })}
                          />
                          {errors.due_date && <div className="invalid-feedback">Date is required</div>}
                        </div>
                      </div>
                      <div className="col">
                        <div className="form-group">
                          <label className="form-label required">Time</label>
                          <input
                            type="time"
                            className={`form-control ${errors.due_time ? 'is-invalid' : ''}`}
                            {...register("due_time", { required: true })}
                          />
                          {errors.due_time && <div className="invalid-feedback">Time is required</div>}
                        </div>
                      </div>
                    </div>

                    <div className='row'>
                      <div className='col-12 footer-btns text-right'>
                        <button type="button" className="btn btn-outline mr-2" onClick={() => setViewMode(true)}>
                          {translation('key_cancel')}
                        </button>
                        <SubmitSpinnerButton status={submitStatus} submitText={translation('key_save')} />
                      </div>
                    </div>
                  </form>
                )}
              </>
            }
          </div>
        </div>
      </div>
    </div>
  );
}

PaymentFollowUpPopupView.propTypes = {
  onClose: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool,
  paymentFollowUpId: PropTypes.number,
};

export default PaymentFollowUpPopupView;